/**
 * Module-level registry for one-shot "account created" callbacks.
 *
 * The account create dialog is opened through Redux (`openDialog`). Redux state
 * is managed by Immer (auto-freeze) and is meant to hold serialisable data only,
 * so we cannot safely stash a function in the dialog payload. Instead the opener
 * registers a callback here under a key, passes only that key in the payload, and
 * the dialog fires (and clears) the callback once, on successful creation.
 */
const callbacks = new Map();

let seq = 0;

/**
 * Registers a one-shot callback and returns its key.
 * @param {(account: object) => void} cb
 * @returns {string} key to pass in the dialog payload (`onCreatedKey`).
 */
export function registerAccountCreatedCallback(cb: any) {
  const key = `acc-created-cb-${++seq}`;
  callbacks.set(key, cb);
  return key;
}

/**
 * Consumes (fires once and removes) the callback registered under the key.
 * No-op when the key is missing.
 */
export function consumeAccountCreatedCallback(key: any, account: any) {
  if (!key) return;
  const cb = callbacks.get(key);
  if (cb) {
    callbacks.delete(key);
    cb(account);
  }
}

/**
 * Drops a registered callback without firing it (e.g. dialog cancelled).
 */
export function clearAccountCreatedCallback(key: any) {
  if (key) callbacks.delete(key);
}
